/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * CENTRALIZED OPENAI CLIENT & RETRY LOGIC
 * ═══════════════════════════════════════════════════════════════════════════════
 * Shared OpenAI helpers used across all screens.
 * Includes automatic retry with exponential backoff for transient errors
 * (520, 429, 500, 502, 503, 504).
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import OpenAI from 'openai'

const STORAGE_KEY = 'ptp_openai_api_key'

/** Read the saved API key from localStorage */
export function getSavedApiKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

/** Persist an API key to localStorage */
export function saveApiKey(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, key)
  } catch {
    // silently ignore storage errors
  }
}

/** Create a raw OpenAI client instance */
export function getOpenAIClient(): OpenAI {
  const apiKey = getSavedApiKey()
  return new OpenAI({
    apiKey: apiKey || 'missing-key',
    dangerouslyAllowBrowser: true,
  })
}

// ── Retry-enabled wrapper ────────────────────────────────────────────────────

/** HTTP status codes that are transient and worth retrying */
const RETRYABLE_STATUS_CODES = new Set([
  429, 500, 502, 503, 504, 520, 521, 522, 524,
])

/** Maximum number of attempts (1 initial + 2 retries) */
const MAX_ATTEMPTS = 3

/** Base delay in ms — doubled on each retry */
const BASE_DELAY_MS = 1000

function isRetryableError(err: unknown): boolean {
  if (err && typeof err === 'object') {
    // OpenAI SDK attaches `status` on API errors
    const status = (err as any).status ?? (err as any).statusCode
    if (typeof status === 'number' && RETRYABLE_STATUS_CODES.has(status)) {
      return true
    }
    // Check the error message for status codes (some wrappers embed it)
    const msg: string = (err as any).message ?? ''
    if (/status\s*code\s*(429|500|502|503|504|520|521|522|524)/i.test(msg)) {
      return true
    }
    // Network-level failures (fetch failed, ECONNRESET, etc.)
    if (/network|fetch|econnreset|socket hang up|timeout/i.test(msg)) {
      return true
    }
  }
  return false
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Wrapper around `client.chat.completions.create()` with automatic retry
 * for transient server errors (520, 429, 5xx, network failures).
 *
 * Usage:
 * ```ts
 * const completion = await chatCompletionWithRetry({ model, messages, ... })
 * ```
 */
export async function chatCompletionWithRetry(
  params: OpenAI.Chat.Completions.ChatCompletionCreateParamsNonStreaming,
): Promise<OpenAI.Chat.Completions.ChatCompletion> {
  const client = getOpenAIClient()
  let lastError: unknown

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const result = await client.chat.completions.create(params)
      return result
    } catch (err) {
      lastError = err
      const status = (err as any)?.status
      console.warn(
        `[OpenAI] Attempt ${attempt}/${MAX_ATTEMPTS} failed` +
          (status ? ` (status ${status})` : '') +
          `: ${(err as any)?.message ?? err}`,
      )

      if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
        const delay =
          BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.random() * 500
        console.log(`[OpenAI] Retrying in ${Math.round(delay)}ms...`)
        await sleep(delay)
        continue
      }

      // Non-retryable or final attempt — throw immediately
      throw err
    }
  }

  // Should never reach here, but just in case
  throw lastError
}

/**
 * Convenience: transcribe audio via Whisper with retry.
 */
export async function transcribeWithRetry(
  file: File,
  options?: Partial<OpenAI.Audio.Transcriptions.TranscriptionCreateParams>,
): Promise<OpenAI.Audio.Transcriptions.Transcription> {
  const client = getOpenAIClient()
  let lastError: unknown

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const result = await client.audio.transcriptions.create({
        model: 'whisper-1',
        file,
        ...options,
      })
      return result
    } catch (err) {
      lastError = err
      const status = (err as any)?.status
      console.warn(
        `[OpenAI Whisper] Attempt ${attempt}/${MAX_ATTEMPTS} failed` +
          (status ? ` (status ${status})` : '') +
          `: ${(err as any)?.message ?? err}`,
      )

      if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
        const delay =
          BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.random() * 500
        console.log(`[OpenAI Whisper] Retrying in ${Math.round(delay)}ms...`)
        await sleep(delay)
        continue
      }

      throw err
    }
  }

  throw lastError
}
